import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { convertGeneralContextErrorToHookForm } from '@src/common';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { errorsSelector } from '@src/features/customers/auth/store/auth';
import { track } from '@src/services/analytics';

import { usePasswordRecovery } from './usePasswordRecovery';
import { recoveryEmailSchema, type RecoveryEmailFormValues } from './recoveryEmailSchema';

type UseRecoveryEmailFormParams = {
  initialEmail: string;
};

export const useRecoveryEmailForm = (params: UseRecoveryEmailFormParams) => {
  const { initialEmail } = params;
  const normalizedInitialEmail = initialEmail ?? '';
  const { i18n } = useTranslate();
  const contextErrors = useAppSelector(errorsSelector);
  const { sendResetCode, loading } = usePasswordRecovery();
  const schema = useMemo(() => recoveryEmailSchema(i18n), [i18n]);

  const {
    control,
    setValue,
    setError,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RecoveryEmailFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: { email: normalizedInitialEmail },
  });

  const email = watch('email') ?? '';

  useEffect(() => {
    setValue('email', normalizedInitialEmail);
  }, [normalizedInitialEmail, setValue]);

  useEffect(() => {
    convertGeneralContextErrorToHookForm('email', setError, contextErrors, 'password_reset');
  }, [contextErrors, setError]);

  useEffect(() => {
    track('recoveryPasswordEmailStepAccess');
  }, []);

  const isEmailValid = schema.shape.email.safeParse(email).success;
  const isSubmitEnabled = isEmailValid;

  const onSubmit = handleSubmit((data) => {
    track('recoveryPasswordEmailStepSubmit');
    sendResetCode(data.email);
  });

  return {
    control,
    errors,
    loading,
    isSubmitEnabled,
    onSubmit,
  };
};
